/**
 * MontillaAds link ids — the client-side foundation for our own ads tool.
 *
 * A link id is a single opaque, URL-safe token that encodes which app +
 * campaign + creative generated a click, plus a nonce for click-level
 * uniqueness. It travels in the `mad` URL param and is decoded back into flat
 * session attributes on capture (see the ad-attribution store).
 *
 * Format (fixed 5 dot-separated segments; `_` marks an absent optional part):
 *   mad.<app>.<campaign>.<creative>.<nonce>
 *   e.g. mad.quero-bolsa.black-friday._.k3f9ab
 *
 * Dots are safe separators: every segment is slugified (alnum + hyphen) and
 * the nonce is base36, so neither can contain a dot.
 */

/** Marker/version prefix — bump if the segment layout ever changes. */
const AD_ID_PREFIX = 'mad'
const AD_ID_SEGMENTS = 5
/** Placeholder for an absent optional segment (campaign/creative). */
const EMPTY_SEGMENT = '_'
/** Max slug length per segment — keeps URLs sane for long campaign names. */
const MAX_SEGMENT_LEN = 48

/** Decoded parts of an ad link id. */
export interface AdLinkParts {
  app: string
  campaign?: string
  creative?: string
  nonce: string
}

/** Inputs for {@link buildAdLinkId}. `app` is required; the rest are optional. */
export interface AdLinkInput {
  app: string
  campaign?: string
  creative?: string
}

/**
 * Slugify one segment: lowercase, collapse any run of non-alphanumerics to a
 * single hyphen, trim leading/trailing hyphens, and cap the length. Returns an
 * empty string when nothing usable remains.
 */
export function slugifyAdSegment(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents (combining marks)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SEGMENT_LEN)
    .replace(/-+$/g, '') // re-trim in case the slice cut mid-hyphen
}

/**
 * Short, URL-safe, non-guessable nonce (base36) for click-level uniqueness.
 * Uses crypto when available; falls back to a time-seeded value so it never
 * throws in exotic environments.
 */
export function adNonce(length = 6): string {
  const alphabet = '0123456789abcdefghijklmnopqrstuvwxyz'
  let out = ''
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(length)
      crypto.getRandomValues(bytes)
      for (let i = 0; i < length; i++) out += alphabet[(bytes[i] ?? 0) % 36]
      return out
    }
  } catch {
    // fall through to the non-crypto path
  }
  // Non-crypto fallback (analytics id, not a secret): time + counter-ish.
  let seed = Date.now()
  for (let i = 0; i < length; i++) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    out += alphabet[seed % 36]
  }
  return out
}

/**
 * Build an opaque ad link id from an app (+ optional campaign/creative).
 * Always returns a 5-segment id so {@link parseAdLinkId} can rely on position.
 */
export function buildAdLinkId(input: AdLinkInput): string {
  const seg = (v?: string) => slugifyAdSegment(v ?? '') || EMPTY_SEGMENT
  return [
    AD_ID_PREFIX,
    seg(input.app),
    seg(input.campaign),
    seg(input.creative),
    adNonce(),
  ].join('.')
}

/**
 * Parse an ad link id back into its parts. Returns `null` for anything that
 * isn't a well-formed MontillaAds id, so callers can safely ignore junk params.
 */
export function parseAdLinkId(id: string | null | undefined): AdLinkParts | null {
  if (!id) return null
  const parts = id.split('.')
  if (parts.length !== AD_ID_SEGMENTS || parts[0] !== AD_ID_PREFIX) return null
  const [, app, campaign, creative, nonce] = parts
  if (!app || app === EMPTY_SEGMENT || !nonce) return null
  return {
    app,
    ...(campaign && campaign !== EMPTY_SEGMENT ? { campaign } : {}),
    ...(creative && creative !== EMPTY_SEGMENT ? { creative } : {}),
    nonce,
  }
}

/**
 * Flatten decoded ad parts into the `montilla_ad_*` session attributes that
 * ride along with `utm_*` to every destination. `montilla_ad_id` is the raw
 * token.
 */
export function adAttributionAttrs(id: string, parts: AdLinkParts): Record<string, string> {
  return {
    montilla_ad_id: id,
    montilla_ad_app: parts.app,
    ...(parts.campaign ? { montilla_ad_campaign: parts.campaign } : {}),
    ...(parts.creative ? { montilla_ad_creative: parts.creative } : {}),
  }
}
